import { useEffect, useMemo, useState } from 'react';
import DayPicker, {
  DateUtils,
  DayPickerProps,
  Modifier,
} from 'react-day-picker';

import { getYear, startOfToday, format, startOfMonth } from 'date-fns';

// types
import {
  RangeType,
  RangeOutputType,
  DateRangePickerProps,
  DatePickerMode,
} from './types';
import { ButtonSize, ButtonType } from '../button/types';

// components
import ButtonPrimary from '../button/button-primary';
import ButtonPseudo from '../button/button-pseudo';
import YearMonthSelector from './year-month-selector';
import CustomSelector from './custom-selector';
import InfiniteScrollLoop from './loop-scroll';

import usePrevious from '../../hooks/use-previous';

// styles
import styles from './rangepicker.module.css';

export interface CaptionElementProps {
  date: Date;
  localeUtils: {
    getMonths: () => string[];
  };
}

const getMonth = (date: Date, direction: 'left' | 'right'): Date => {
  const nextMonth = new Date(
    new Date(date).setMonth(date.getMonth() + (direction === 'left' ? -1 : 1)),
  );

  return nextMonth;
};

const renderYearMonthSelectorComponent = (
  date: Date,
  localeUtils: {
    getMonths: () => string[];
  },
  handleChange: (value: Date) => void,
  yearsToSelect: number[],
  title?: string,
  withTitle?: boolean,
) => (
  <>
    {title && withTitle && <div className={styles.pickerTitle}>{title}</div>}
    <YearMonthSelector
      date={date}
      localeUtils={localeUtils}
      onChange={handleChange}
      years={yearsToSelect}
    />
  </>
);

export default function RangePicker({
  startDate,
  endDate,
  value,
  onApply,
  onReset,
  twoPickers,
  onSingleApply,
  withoutCustomSelector = false,
  mode = DatePickerMode.RANGE,
  disabledAfter = new Date(2099, 0, 1),
  prevDatepickerVisible,
  datepickerVisible,
  filterPlace,
  birthdayPicker,
  title,
  withTitle,
  withResetClose,
  setDatepickerVisible,
  withTime,
  withTimeSelector,
  withTwoTimeSelector,
  onTimeClick,
}: DateRangePickerProps): React.JSX.Element {
  const today = startOfToday();
  const minBirthdayDate = new Date(
    new Date().getFullYear() - 18,
    new Date().getMonth(),
    new Date().getDate(),
  );

  const currentMonth = startOfMonth(birthdayPicker ? minBirthdayDate : today);

  const initialState = {
    from: undefined,
    to: undefined,
  };

  const [currentDate, setCurrentDate] = useState(currentMonth);
  const [currentMonthValue, setCurrentMonth] = useState(currentMonth);
  const [nextMonth, setNextMonth] = useState(getMonth(currentMonth, 'right'));
  const [range, setRange] = useState<RangeType>(initialState);
  const [visibleRange, setVisibleRange] = useState<RangeType>(initialState);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [customPeriodSelected, setCustomPeriodSelected] = useState('');
  const [timeValue, setTimeValue] = useState<{ hour: string; minute: string }>({
    hour: '',
    minute: '',
  });
  const [timeValueTo, setTimeValueTo] = useState<{
    hour: string;
    minute: string;
  }>({
    hour: '',
    minute: '',
  });

  const prevValue: string[] = usePrevious(value);

  const yearsToSelect: number[] = [];

  for (let i = getYear(startDate); i <= getYear(endDate); i += 1) {
    yearsToSelect.push(i);
  }

  const prevSelectedDate = usePrevious(selectedDate);

  const { from, to } = visibleRange;
  const modifiers = { start: from, end: to };

  useEffect(() => {
    if (value?.length) {
      if (mode === DatePickerMode.RANGE) {
        const dateFrom = new Date(value[0]);

        if (dateFrom) {
          const nextRangeObject: {
            from: Date;
            to: undefined | Date;
          } = {
            from: dateFrom,
            to: undefined,
          };

          setCurrentDate(dateFrom);
          setCurrentMonth(dateFrom);

          setNextMonth(getMonth(dateFrom, 'right'));

          if (value?.length > 1) {
            const dateTo = new Date(value[1]);

            if (dateTo) {
              nextRangeObject.to = dateTo;

              if (dateFrom.getMonth() === dateTo.getMonth()) {
                setNextMonth(getMonth(dateTo, 'right'));
              } else {
                setNextMonth(dateTo);
              }

              setCurrentDate(getMonth(dateTo, 'left'));
            }
          }

          setVisibleRange(nextRangeObject);
          setRange(nextRangeObject);
        }
      } else if (mode === DatePickerMode.SINGLE) {
        if (!selectedDate) {
          const date = new Date(value[0]);
          setSelectedDate(date);

          setCurrentMonth(date);
          setCurrentDate(date);
          setNextMonth(getMonth(date, 'right'));
        }
      }
    } else if (
      !value?.length &&
      prevValue &&
      prevValue?.length &&
      mode === DatePickerMode.RANGE
    ) {
      setRange({
        from: undefined,
        to: undefined,
      });
      setVisibleRange({
        from: undefined,
        to: undefined,
      });
    }
  }, [value, mode, prevValue, selectedDate]);

  const handleYearMonthChange = (monthValue: Date) => {
    setCurrentDate(monthValue);
    setCurrentMonth(monthValue);

    setNextMonth(getMonth(monthValue, 'right'));
  };

  const handleSecondPickerYearMonthChange = (monthValue: Date) => {
    setNextMonth(monthValue);

    setCurrentDate(getMonth(monthValue, 'left'));
    setCurrentMonth(getMonth(monthValue, 'left'));
  };

  const handleDayClick = (day: Date) => {
    setCustomPeriodSelected('');

    if (mode === DatePickerMode.RANGE) {
      const nextRange = DateUtils.addDayToRange(new Date(day), range);

      setRange(nextRange as RangeType);
      setVisibleRange(nextRange as RangeType);
    } else {
      setSelectedDate(day);
    }
  };

  const handleCustomDateChange = (customValue: RangeType | Date) => {
    if ('from' in customValue) {
      setRange(customValue);
      setVisibleRange(customValue);
    } else {
      setSelectedDate(customValue);
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();

      if (withResetClose && setDatepickerVisible) {
        setDatepickerVisible(false);
      }

      if (onTimeClick) {
        onTimeClick(false);
      }
    }

    setRange(initialState);
    setVisibleRange(initialState);
    setSelectedDate(undefined);

    const defaultDate = birthdayPicker ? minBirthdayDate : today;
    setCurrentDate(defaultDate);
    setCurrentMonth(defaultDate);
    setNextMonth(getMonth(startOfMonth(defaultDate), 'right'));

    if (onReset) {
      onReset();
    }
  };

  const handleApply = () => {
    // data output point

    if (mode === DatePickerMode.RANGE) {
      const result: RangeOutputType = [];

      if (from) {
        const dateFrom = new Date(range?.from as Date);

        if (withTimeSelector && withTwoTimeSelector) {
          if (timeValue.hour) {
            dateFrom.setHours(Number(timeValue.hour));
          }

          if (timeValue.minute) {
            dateFrom.setMinutes(Number(timeValue.minute));
          }
        } else {
          dateFrom.setHours(0);
          dateFrom.setMinutes(0);
          dateFrom.setSeconds(0);
        }

        result.push(dateFrom);
      }

      if (to) {
        const dateTo = new Date(range?.to as Date);

        if (withTimeSelector && withTwoTimeSelector) {
          if (timeValueTo.hour) {
            dateTo.setHours(Number(timeValueTo.hour));
          }

          if (timeValueTo.minute) {
            dateTo.setMinutes(Number(timeValueTo.minute));
          }
        } else {
          dateTo.setHours(23);
          dateTo.setMinutes(59);
          dateTo.setSeconds(59);
        }

        result.push(dateTo);
      }

      if (onApply) {
        if (customPeriodSelected === 'For all time') {
          const dateFrom = new Date(1970, 0, 1);
          const dateTo = new Date();
          dateFrom.setHours(0);
          dateFrom.setMinutes(0);
          dateFrom.setSeconds(0);
          dateTo.setHours(23);
          dateTo.setMinutes(59);
          dateTo.setSeconds(59);

          onApply([dateFrom, dateTo]);
        } else {
          onApply(result);
        }
      }
    } else {
      const result = selectedDate;

      if (withTime && result && !withTimeSelector) {
        result.setHours(new Date().getHours());
        result.setMinutes(new Date().getMinutes());
      }

      if (withTimeSelector && result) {
        if (timeValue.hour) {
          result.setHours(Number(timeValue.hour));

          if (onTimeClick) {
            onTimeClick(true);
          }
        }

        if (timeValue.minute) {
          result.setMinutes(Number(timeValue.minute));

          if (onTimeClick) {
            onTimeClick(true);
          }
        }
      }

      if (onSingleApply) {
        onSingleApply(result as Date);
      }
    }
  };

  useEffect(() => {
    if (withTimeSelector && value && value[0] && !timeValue.hour && withTime) {
      const date = new Date(value[0]);

      setTimeValue({
        hour: date.getHours().toString(),
        minute: date.getMinutes().toString(),
      });

      if (withTwoTimeSelector && value[1] && !timeValueTo.hour) {
        const dateTo = new Date(value[1]);

        setTimeValueTo({
          hour: dateTo.getHours().toString(),
          minute: dateTo.getMinutes().toString(),
        });
      }
    }
  }, [withTimeSelector, value, withTwoTimeSelector, withTime]);

  useEffect(() => {
    if (!prevDatepickerVisible && datepickerVisible) {
      setRange(initialState);
    }
  }, [prevDatepickerVisible, datepickerVisible]);

  const content = useMemo(() => {
    let selectedDays;

    if (mode === DatePickerMode.RANGE) {
      selectedDays = [null, { from, to }];
    } else {
      selectedDays = new Date(selectedDate as Date);
    }

    const data = (
      <>
        <DayPicker
          // navbarElement={<Navbar />}
          month={currentMonthValue}
          classNames={styles as unknown as DayPickerProps['classNames']}
          selectedDays={selectedDays as Modifier[]}
          modifiers={modifiers}
          disabledDays={
            birthdayPicker
              ? { after: minBirthdayDate }
              : { after: disabledAfter }
          }
          onDayClick={handleDayClick}
          onMonthChange={(date) => {
            if (twoPickers) {
              setNextMonth(getMonth(date, 'right'));
            }
          }}
          captionElement={({ date, localeUtils }: CaptionElementProps) => {
            let dateValue = date;

            if (
              mode !== DatePickerMode.RANGE &&
              selectedDate &&
              prevSelectedDate !== selectedDate
            ) {
              dateValue = selectedDate;
            }

            return renderYearMonthSelectorComponent(
              dateValue,
              localeUtils,
              handleYearMonthChange,
              yearsToSelect,
              title,
              withTitle,
            );
          }}
          showOutsideDays
        />
        {twoPickers && (
          <DayPicker
            month={nextMonth}
            classNames={styles as unknown as DayPickerProps['classNames']}
            selectedDays={selectedDays as Modifier[]}
            modifiers={modifiers}
            onDayClick={handleDayClick}
            disabledDays={
              birthdayPicker ? { after: minBirthdayDate } : disabledAfter
            }
            onMonthChange={(date) => {
              if (twoPickers) {
                setCurrentDate(getMonth(date, 'left'));
              }
            }}
            captionElement={({ date, localeUtils }: CaptionElementProps) => {
              let dateValue = date;

              if (
                mode !== DatePickerMode.RANGE &&
                selectedDate &&
                prevSelectedDate !== selectedDate
              ) {
                dateValue = selectedDate;
              }

              return renderYearMonthSelectorComponent(
                dateValue,
                localeUtils,
                handleSecondPickerYearMonthChange,
                yearsToSelect,
              );
            }}
            showOutsideDays
          />
        )}
      </>
    );

    return data;
  }, [
    selectedDate,
    from,
    to,
    currentDate,
    nextMonth,
    mode,
    prevSelectedDate,
    currentMonthValue,
  ]);

  return (
    <div
      className={`${styles.rangepickerWrap} ${
        mode === DatePickerMode.SINGLE ? styles.rangepickerSingle : ''
      }`}
    >
      <div>
        <div className={styles.rangepickerContent}>{content}</div>
        <div
          className={`${styles.rangepickerFooter} ${
            filterPlace ? styles.rangepickerFooterWithPadding : ''
          }`}
        >
          <div className={styles.rangepickerFooterButtons}>
            <ButtonPrimary
              text="Apply"
              size={ButtonSize.SMALL}
              onClick={handleApply}
              type={ButtonType.SUBMIT}
            />
            <ButtonPseudo
              text="Reset"
              size={ButtonSize.SMALL}
              onClick={() => handleReset()}
            />
            {withTime && withTimeSelector && onTimeClick && (
              <ButtonPseudo
                text="Reset time"
                size={ButtonSize.SMALL}
                onClick={() => {
                  onTimeClick(false);
                  setTimeValue({ hour: '', minute: '' });
                }}
              />
            )}
          </div>
          <div>
            {!withTime && (
              <>
                {mode === DatePickerMode.RANGE
                  ? (range.from || customPeriodSelected) && (
                      <div className={styles.rangepickerFooterRange}>
                        {customPeriodSelected ||
                          `${from && format(from, 'yyyy-MM-dd')} ${
                            to && to !== undefined
                              ? ` - ${format(to, 'yyyy-MM-dd')}`
                              : ''
                          }`}
                      </div>
                    )
                  : (selectedDate || customPeriodSelected) && (
                      <div className={styles.rangepickerFooterRange}>
                        {customPeriodSelected ||
                          format(new Date(selectedDate as Date), 'yyyy-MM-dd')}
                      </div>
                    )}
              </>
            )}
          </div>
        </div>
      </div>
      {withTimeSelector && (
        <div className={styles.timeSelectors}>
          <div>
            {withTwoTimeSelector && (
              <div className={styles.timeSelectorTitle}>Time from</div>
            )}
            <div
              className={`${styles.timeSelectorWrapper} ${
                withTwoTimeSelector ? styles.withPadding : ''
              }`}
            >
              <InfiniteScrollLoop
                className={styles.timeSelectorList}
                type="hour"
                currentTime={
                  timeValue.hour
                    ? Number(timeValue.hour)
                    : new Date().getHours()
                }
              >
                {[
                  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                  18, 19, 20, 21, 22, 23,
                ].map((item) => (
                  <button
                    type="button"
                    className={`hour-button-${item} ${styles.timeButton} ${
                      new Date().getHours() === item
                        ? `${styles.currentTime} button-active`
                        : ''
                    } ${
                      timeValue.hour && Number(timeValue.hour) === item
                        ? styles.active
                        : ''
                    }`}
                    key={item}
                    onClick={() =>
                      setTimeValue({ ...timeValue, hour: item.toString() })
                    }
                  >
                    {`${item < 10 ? '0' : ''}${item}`}
                  </button>
                ))}
              </InfiniteScrollLoop>
              <InfiniteScrollLoop
                className={styles.timeSelectorList}
                type="minute"
                currentTime={
                  timeValue.minute
                    ? Number(timeValue.minute)
                    : new Date().getMinutes()
                }
              >
                {[
                  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                  18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
                  33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
                  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59,
                ].map((item) => (
                  <button
                    type="button"
                    className={`minute-button-${item} ${styles.timeButton} ${
                      new Date().getMinutes() === item
                        ? `${styles.currentTime} button-active`
                        : ''
                    } ${
                      timeValue.minute && Number(timeValue.minute) === item
                        ? styles.active
                        : ''
                    }`}
                    key={item}
                    onClick={() =>
                      setTimeValue({ ...timeValue, minute: item.toString() })
                    }
                  >
                    {`${item < 10 ? '0' : ''}${item}`}
                  </button>
                ))}
              </InfiniteScrollLoop>
            </div>
          </div>
          {withTwoTimeSelector && (
            <>
              <div className={styles.separatorWrapper}>
                <div className={styles.separator} />
              </div>
              <div>
                <div className={styles.timeSelectorTitle}>Time to</div>
                <div
                  className={`${styles.timeSelectorWrapper} ${
                    withTwoTimeSelector ? styles.withPadding : ''
                  }`}
                >
                  <InfiniteScrollLoop
                    className={styles.timeSelectorList}
                    type="hour"
                    currentTime={
                      timeValueTo.hour
                        ? Number(timeValueTo.hour)
                        : new Date().getHours()
                    }
                  >
                    {[
                      0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
                      17, 18, 19, 20, 21, 22, 23,
                    ].map((item) => (
                      <button
                        type="button"
                        className={`hour-button-${item} ${styles.timeButton} ${
                          new Date().getHours() === item
                            ? `${styles.currentTime} button-active`
                            : ''
                        } ${
                          timeValueTo.hour && Number(timeValueTo.hour) === item
                            ? styles.active
                            : ''
                        }`}
                        key={item}
                        onClick={() =>
                          setTimeValueTo({
                            ...timeValueTo,
                            hour: item.toString(),
                          })
                        }
                      >
                        {`${item < 10 ? '0' : ''}${item}`}
                      </button>
                    ))}
                  </InfiniteScrollLoop>
                  <InfiniteScrollLoop
                    className={styles.timeSelectorList}
                    type="minute"
                    currentTime={
                      timeValueTo.minute
                        ? Number(timeValueTo.minute)
                        : new Date().getMinutes()
                    }
                  >
                    {[
                      0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
                      17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30,
                      31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44,
                      45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58,
                      59,
                    ].map((item) => (
                      <button
                        type="button"
                        className={`minute-button-${item} ${
                          styles.timeButton
                        } ${
                          new Date().getMinutes() === item
                            ? `${styles.currentTime} button-active`
                            : ''
                        } ${
                          timeValueTo.minute &&
                          Number(timeValueTo.minute) === item
                            ? styles.active
                            : ''
                        }`}
                        key={item}
                        onClick={() =>
                          setTimeValueTo({
                            ...timeValueTo,
                            minute: item.toString(),
                          })
                        }
                      >
                        {`${item < 10 ? '0' : ''}${item}`}
                      </button>
                    ))}
                  </InfiniteScrollLoop>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {!withoutCustomSelector && (
        <CustomSelector
          handleCustomDateChange={handleCustomDateChange}
          mode={mode}
          setCustomPeriodSelected={setCustomPeriodSelected}
        />
      )}
    </div>
  );
}
